import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button
} from '@mui/material';
import Logo from '../../assets/logo_jothi.jpg';

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to safely format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "₹0.00";
  const numValue = parseFloat(value);
  return isNaN(numValue) ? "₹0.00" : `₹${numValue.toFixed(2)}`;
};

// Helper function to check if a date falls within the selected range
const isDateInRange = (transactionDate, fromDate, toDate) => {
  if (!transactionDate) return false;

  const txnDate = new Date(transactionDate);
  const from = fromDate ? new Date(fromDate) : null;
  const to = toDate ? new Date(toDate) : null;

  // Set time to start of day for accurate comparison
  txnDate.setHours(0, 0, 0, 0);
  if (from) from.setHours(0, 0, 0, 0);
  if (to) to.setHours(23, 59, 59, 999);

  if (from && to) {
    return txnDate >= from && txnDate <= to;
  } else if (from) {
    return txnDate >= from;
  } else if (to) {
    return txnDate <= to;
  }

  return true; // If no date filters, include all transactions
};

const TransactionPDF = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [totalDeposits, setTotalDeposits] = useState(0);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [formattedTransactions, setFormattedTransactions] = useState([]);

  const calculateClosingBalance = (transactions) => {
    if (!transactions || transactions.length === 0) {
      return { transactions: [], totalDeposits: 0, totalWithdrawals: 0, closingBalance: 0 };
    }

    let balance = 0;
    let totalDeposits = 0;
    let totalWithdrawals = 0;

    const formattedTransactions = transactions.map((transaction) => {
      // First check for directly stored credit/debit values in the transaction
      let credit = transaction.credit ? parseFloat(transaction.credit) : 0;
      let debit = transaction.debit ? parseFloat(transaction.debit) : 0;

      // If credit/debit are not directly available, determine them from transactionType and amount
      if ((credit === 0 && debit === 0) && transaction.amount) {
        credit = transaction.transactionType === "deposit" ? parseFloat(transaction.amount) : 0;
        debit = transaction.transactionType === "withdraw" ? parseFloat(transaction.amount) : 0;
      }

      // Ensure they're valid numbers
      credit = isNaN(credit) ? 0 : credit;
      debit = isNaN(debit) ? 0 : debit;

      balance += credit - debit;
      totalDeposits += credit;
      totalWithdrawals += debit;

      return {
        ...transaction,
        credit,
        debit,
        closingBalance: balance
      };
    });

    return {
      transactions: formattedTransactions,
      totalDeposits,
      totalWithdrawals,
      closingBalance: balance,
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!location.state || !location.state.customerData) {
        navigate('/memberdetails');
        return;
      }

      const { customerData, transactions, fromDate, toDate } = location.state;
      setCustomerData(customerData);
      setDateRange({ from: fromDate, to: toDate });

      if (transactions && transactions.length > 0) {
        // Transactions are already filtered in Memberdetails.js before navigation
        const sortedFiltered = [...transactions].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        // Calculate totals for the period without overwriting running balances
        let periodDeposits = 0;
        let periodWithdrawals = 0;
        sortedFiltered.forEach(t => {
          let credit = t.credit ? parseFloat(t.credit) : 0;
          let debit = t.debit ? parseFloat(t.debit) : 0;
          if (credit === 0 && debit === 0 && t.amount) {
            credit = t.transactionType === "deposit" ? parseFloat(t.amount) : 0;
            debit = t.transactionType === "withdraw" ? parseFloat(t.amount) : 0;
          }
          periodDeposits += (isNaN(credit) ? 0 : credit);
          periodWithdrawals += (isNaN(debit) ? 0 : debit);
        });
        
        // The closing balance is the balance of the last transaction in the filtered list
        const periodClosingBalance = sortedFiltered.length > 0 
          ? sortedFiltered[sortedFiltered.length - 1].closingBalance 
          : 0;
        
        setTransactions(transactions);
        setFilteredTransactions(sortedFiltered);
        setFormattedTransactions(sortedFiltered);
        setTotalDeposits(periodDeposits);
        setTotalWithdrawals(periodWithdrawals);
        setClosingBalance(periodClosingBalance);
      }

      setLoading(false);
    };

    fetchData();
  }, [location, navigate]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/memberdetails');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!customerData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>No customer data available. <Button onClick={handleBack}>Go Back</Button></Typography>
      </Box>
    );
  }

  return (
    <Box style={{ padding: "20px", backgroundColor: "#fff", width: "100%", boxSizing: "border-box" }}>
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            #print-content {
              width: 190mm;
              padding: 0;
              box-sizing: border-box;
              font-size: 9px;
            }
            .no-print {
              display: none !important;
            }
            table {
              width: 100%;
              font-family: 'Poppins', sans-serif;
              font-size: 8px;
              border-collapse: collapse;
              table-layout: fixed;
              border: 1.5px solid #000;
              letter-spacing: 0.3px;
            }
            th, td {
              padding: 3px 4px !important;
              border: 1px solid #000 !important;
              word-wrap: break-word;
              word-break: break-all;
              overflow: hidden;
              font-family: 'Poppins', sans-serif;
              font-size: 8px !important;
              line-height: 1.2;
              white-space: nowrap;
            }
            th {
              background-color: #f5f5f5 !important;
              font-weight: bold !important;
              font-size: 8.5px !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .col-serial { width: 8%; }
            .col-date { width: 13%; }
            .col-txn-id { width: 23%; }
            .col-desc { width: 20%; white-space: normal; }
            .col-credit { width: 13%; }
            .col-debit { width: 13%; }
            .col-balance { width: 10%; }
            #print-content h6, #print-content p, #print-content span {
              font-size: 9px !important;
            }
          }
        `}
      </style>

      <Box id="print-content">
        <Box style={{ textAlign: "center", marginBottom: "10px" }}>
          <img src={Logo} alt="Jothilinga Logo" style={{ width: "120px", height: "auto" }} />
        </Box>
        <Typography style={{ fontSize: "18px", fontWeight: "bold", textAlign: "center", marginBottom: "10px" }}>
          JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD
        </Typography>
        <Typography style={{ fontSize: "16px", fontWeight: "bold", textAlign: "center", marginBottom: "10px" }}>
          Transaction Statement
        </Typography>
        <Typography style={{ fontSize: "14px", textAlign: "center", marginBottom: "5px" }}>
          {customerData.name} ({customerData.customerUniqueId})
        </Typography>
        <Typography style={{ fontSize: "12px", textAlign: "center", marginBottom: "5px" }}>
          Mobile: {customerData.mobileNumber} | Address: {customerData.permanentStreetAddress || "N/A"}
        </Typography>

        {/* Show date range if available */}
        {(dateRange.from || dateRange.to) && (
          <Typography style={{ fontSize: "12px", textAlign: "center", marginBottom: "5px" }}>
            Period: {dateRange.from ? formatDate(dateRange.from) : 'Beginning'} to {dateRange.to ? formatDate(dateRange.to) : 'End'}
          </Typography>
        )}

        <Typography style={{ fontSize: "12px", textAlign: "center", marginBottom: "10px" }}>
          Generated on: {formatDate(new Date().toISOString())}
        </Typography>

        {formattedTransactions.length === 0 ? (
          <Box style={{ textAlign: "center", marginTop: "20px", marginBottom: "20px" }}>
            <Typography style={{ fontSize: "14px", fontWeight: "bold" }}>
              No transactions found for the selected date range.
            </Typography>
          </Box>
        ) : (
          <Box style={{ marginTop: "10px", width: "100%" }}>
            <Table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell className="col-serial" style={{ fontWeight: "bold", textAlign: "center", fontSize: "10px" }}>
                    No
                  </TableCell>
                  <TableCell className="col-date" style={{ fontWeight: "bold", textAlign: "center", fontSize: "10px" }}>
                    Date
                  </TableCell>
                  <TableCell className="col-txn-id" style={{ fontWeight: "bold", textAlign: "center", fontSize: "10px" }}>
                    Txn ID
                  </TableCell>
                  <TableCell className="col-desc" style={{ fontWeight: "bold", textAlign: "center", fontSize: "10px" }}>
                    Description
                  </TableCell>
                  <TableCell className="col-credit" style={{ fontWeight: "bold", textAlign: "center", fontSize: "10px" }}>
                    Credit
                  </TableCell>
                  <TableCell className="col-debit" style={{ fontWeight: "bold", textAlign: "center", fontSize: "10px" }}>
                    Debit
                  </TableCell>
                  <TableCell className="col-balance" style={{ fontWeight: "bold", textAlign: "center", fontSize: "10px" }}>
                    Balance
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formattedTransactions.map((transaction, index) => (
                  <TableRow key={index}>
                    <TableCell className="col-serial" style={{ textAlign: "center", fontSize: "10px", fontWeight: "bold" }}>
                      {index + 1}
                    </TableCell>
                    <TableCell className="col-date" style={{ textAlign: "center", fontSize: "10px", fontWeight: "bold" }}>
                      {formatDate(transaction.date)}
                    </TableCell>
                    <TableCell className="col-txn-id" style={{ textAlign: "center", fontSize: "10px", fontWeight: "bold", wordBreak: "break-all" }}>
                      {transaction.transactionId || "N/A"}
                    </TableCell>
                    <TableCell className="col-desc" style={{ textAlign: "center", fontSize: "10px", fontWeight: "bold" }}>
                      {transaction.narration || "N/A"}
                    </TableCell>
                    <TableCell className="col-credit" style={{ textAlign: "center", fontSize: "10px", fontWeight: "bold" }}>
                      {transaction.credit > 0 ? formatCurrency(transaction.credit) : "-"}
                    </TableCell>
                    <TableCell className="col-debit" style={{ textAlign: "center", fontSize: "10px", fontWeight: "bold" }}>
                      {transaction.debit > 0 ? formatCurrency(transaction.debit) : "-"}
                    </TableCell>
                    <TableCell className="col-balance" style={{ textAlign: "center", fontSize: "10px", fontWeight: "bold" }}>
                      {formatCurrency(transaction.closingBalance)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Box style={{ marginTop: "2px" }}>
          <Typography style={{ fontSize: "12px" }}>
            <strong>Total Deposits:</strong> {formatCurrency(totalDeposits)}
          </Typography>
          <Typography style={{ fontSize: "12px" }}>
            <strong>Total Withdrawals:</strong> {formatCurrency(totalWithdrawals)}
          </Typography>
          <Typography style={{ fontSize: "12px" }}>
            <strong>Closing Balance:</strong> {formatCurrency(closingBalance)}
          </Typography>
          <Typography style={{ fontSize: "12px" }}>
            <strong>Total Transactions:</strong> {formattedTransactions.length}
          </Typography>
        </Box>

        <Box style={{ marginTop: "15px", display: "flex", justifyContent: "space-between" }}>
          <Typography style={{ fontSize: "10px" }}>
            <strong>Customer Signature</strong>
          </Typography>
          <Typography style={{ fontSize: "10px" }}>
            <strong>Authorized Signature</strong>
          </Typography>
        </Box>
      </Box>

      <Box style={{ marginTop: "20px", display: "flex", justifyContent: "space-between" }} className="no-print">
        <Button variant="contained" onClick={handleBack}>
          Back to Member Details
        </Button>
        <Button variant="contained" color="primary" onClick={handlePrint}>
          Print Document
        </Button>
      </Box>
    </Box>
  );
};

export default TransactionPDF;